/**
 * The shapes the two TM decks consume — mirroring what
 * `transaction.monitoring.api` returns, which is its mongo documents.
 *
 * WHY camelCase AND NOT DIDIT'S snake_case. An earlier draft of this file
 * modelled Didit's documented response objects verbatim, on the assumption the
 * panel would call the vendor directly. It does not: `transaction.monitoring.api`
 * sits in between, owns the rule catalogue and the alert lifecycle, and returns
 * its own documents like every other service in this repo. Mirroring the vendor
 * here would mean a rename layer in the middle whose only job is to undo the
 * service's own convention.
 *
 * The vendor vocabulary is not lost: the SERVICE keeps it. Its ingest payload
 * (`ICreateTransaction`) takes Didit's field names, and its rule catalogue
 * carries Didit's rule names, bundles and categories — so switching a business
 * to vendor-side evaluation is a change behind that endpoint, not out here.
 */

export type ITxnStatus = 'APPROVED' | 'IN_REVIEW' | 'DECLINED' | 'AWAITING_USER';
export type IDirection = 'INBOUND' | 'OUTBOUND';
export type ICurrencyKind = 'fiat' | 'crypto';
export type ISeverity = 'UNKNOWN' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type IRuleMode = 'ACTIVE' | 'TEST';
export type IEntityType = 'person' | 'company';

export type IAlertStatus =
	| 'OPEN'
	| 'INVESTIGATING'
	| 'AWAITING_USER'
	| 'PENDING_SAR'
	| 'SAR_FILED'
	| 'RESOLVED'
	| 'DISMISSED';

export interface ITag {
	name: string;
	color: string;
	source: string;
}

export interface IRuleRun {
	ruleUuid: string;
	ruleName: string;
	category: string;
	bundle: string;
	mode: IRuleMode;
	matched: boolean;
	/** Always 0 for a TEST rule — it records its match and scores nothing. */
	scoreDelta: number;
	actionSummary: string[];
	matchedPayload: Record<string, unknown> | null;
}

export interface IActivity {
	at: string;
	who: string;
	text: string;
	comment?: boolean;
}

export interface IAlert {
	_id: string;
	businessId: string;
	title: string;
	description: string;
	source: 'RULE' | 'MANUAL';
	status: IAlertStatus;
	assignedTo: string | null;
	/**
	 * OUR SLA clock, derived from severity — the vendor carries none.
	 *
	 * NULL on a HISTORICAL alert. An SLA is a promise about how fast a live event
	 * gets a response, and a back-scored row from a client's uploaded ledger never
	 * had a live event to promise anything against — so it carries no clock rather
	 * than a backdated one, which would render as permanently overdue. Anything
	 * formatting this must handle null; the live queue never sees one, because
	 * `alerts.find` excludes historical alerts from every tab.
	 */
	dueAt: string | null;
	/** Populated by the queue endpoint, a bare id elsewhere.
	    ITransaction carries `alerts?: IAlert[]` in return, so the two reference
	    each other whichever order they are declared in — TS hoists interfaces, the
	    lint rule does not know that. */
	// eslint-disable-next-line no-use-before-define
	linkedTransactionId: ITransaction | string;
	linkedCaseId: string | null;
	resolvedAt: string | null;
	notes: { at: string; who: string; text: string }[];
	createdAt: string;
	/** True while this is a historical finding and not live triage work. */
	historical?: boolean;
	importBatchId?: string | null;
}

/**
 * The four FATF Recommendation 16 completeness facts, as assessed by the caller.
 *
 * EVERY FIELD IS OPTIONAL AND A MISSING ONE MEANS "NOT ASSESSED", which is not
 * the same as `false`. The travel-rule predicates match only on an explicit
 * `false`, so a client who does not collect a fact yet is not treated as
 * non-compliant — and the UI must draw the same distinction rather than
 * rendering an absent flag as a failure.
 */
export interface ITravelRule {
	counterpartyVaspIdentified?: boolean;
	originatorInfoComplete?: boolean;
	beneficiaryInfoComplete?: boolean;
	originatorNameMatchesKyc?: boolean;
}

/** One side of a trade, as stored. */
export interface ITxnLeg {
	amount: string | number;
	currency: string;
	feeAmount?: string | number;
	feeCurrency?: string;
	feePreferredCurrencyAmount?: number;
	currencyKind?: ICurrencyKind;
	preferredCurrencyAmount?: number;
	preferredCurrencyCode?: string;
	counterpartyName?: string;
	counterpartyCountry?: string | null;
	counterpartyUnhosted?: boolean;
	/** R.16 facts about THIS leg. Takes precedence over the transaction's own. */
	travelRule?: ITravelRule;
}

export interface ITransaction {
	_id: string;
	businessId: string;
	txnId: string;
	transactionNumber?: number;
	transactionType: string;
	actionType: string;
	status: ITxnStatus;
	direction: IDirection;

	amount: string;
	currency: string;
	currencyKind: ICurrencyKind;
	/** `payment` (one currency) or `trade` (two legs, usually different). */
	transactionKind?: 'payment' | 'trade';
	/**
	 * The other two of the three figures every transaction has. `amount` above is
	 * the PRINCIPAL; these are the total fee and principal + fee.
	 *
	 * The principal is what the rules score — a fee is the institution's revenue,
	 * not moved value, so it must not push a transfer over a reporting threshold.
	 */
	feeAmount?: string | null;
	feeCurrency?: string | null;
	feePreferredCurrencyAmount?: number;
	preferredCurrencyTotalAmount?: number;
	/** Present only on a trade. The subject SENDS one and RECEIVES the other. */
	legs?: {
		sent?: ITxnLeg;
		received?: ITxnLeg;
	};
	/** The comparable figure every threshold rule read, at the rate that applied
	    when the decision was made. Stored, never recomputed on display. */
	preferredCurrencyAmount: number;
	preferredCurrencyCode: string;
	paymentDetails?: string;
	paymentMethods: Record<string, any>[];

	score: number;
	severity: ISeverity;

	subjectName: string;
	/** OUR id for the party, which is how Didit stores it too. */
	subjectVendorData: string;
	subjectCountry: string;
	subjectEntityType: IEntityType;

	counterpartyName: string;
	counterpartyCountry: string | null;
	counterpartyEntityType: IEntityType;
	counterpartyUnhosted?: boolean;
	/**
	 * R.16 facts about the transfer as a whole.
	 *
	 * READ `engineInput.travelRule` IN PREFERENCE TO THIS. The service stores the
	 * assessed flags on `engineInput` — the record of exactly what the engine
	 * scored — and does not mirror them to a top-level field, so this is
	 * undefined on every row written today. Kept because it is the shape the
	 * ingest payload sends and a future mirror would land here.
	 */
	travelRule?: ITravelRule;
	/** What the engine was actually handed, kept so a decision can be re-read. */
	engineInput?: {
		travelRule?: ITravelRule;
		legs?: Record<string, {travelRule?: ITravelRule}>;
		/**
		 * The sanctions / PEP result the score was computed against.
		 *
		 * Stored on every transaction the engine scores and, until now, declared
		 * nowhere and rendered nowhere — so the drawer could name the rule that
		 * fired ("Sanctions exposure — confirmed match") while the list, the
		 * matched name and the score behind it were unreachable from the UI.
		 */
		screening?: {
			sanctionsMatch?: {
				listName?: string;
				matchedName?: string;
				score?: number;
			} | null;
			pepMatch?: Record<string, unknown> | null;
			riskFactors?: {
				category: string;
				entityName?: string;
				exposureType?: string;
				percentage?: number;
				isHighRisk?: boolean;
			}[];
		} | null;
	};

	/**
	 * WHY THIS WAS BLOCKED ON A COUNTRY, stamped at scoring time so the reason
	 * survives a later change to the business's country settings.
	 * `reason: 'locked'` is an embargo that cannot be switched off — a different
	 * fact from a country the business has simply not enabled.
	 */
	countryCoverage?: {
		blocked?: boolean;
		countryCode?: string;
		reason?: string;
	} | null;
	/** The graded country risk that scored this row, stamped for the same reason. */
	countryRisk?: {
		countryCode?: string;
		tier?: 'high' | 'medium' | 'low';
		score?: number;
	} | null;
	/**
	 * The OFAC wallet-list hit, if the address matched. `programs` is what a
	 * report has to name, so it is carried in full rather than summarised.
	 */
	ofacCryptoHit?: {
		address?: string;
		matchedAddress?: string;
		sourceChainCode?: string;
		normalizedChainCode?: string;
		entityName?: string;
		ofacUid?: string;
		programs?: string[];
		field?: string;
	} | null;
	/** The addresses screened. Absent on every fiat transaction. */
	subjectWalletAddress?: string | null;
	counterpartyWalletAddress?: string | null;
	/**
	 * What monitoring this transaction was charged. `metered: false` carries a
	 * reason, and that reason matters: an import, a business with no
	 * subscription and a billing outage are three different outcomes that have
	 * to be tellable apart when a month's revenue does not add up.
	 */
	billing?: {
		metered?: boolean;
		price?: number;
		usageRecordId?: string | null;
		reason?: string | null;
		at?: string | null;
	};

	tags: ITag[];
	ruleRuns: IRuleRun[];
	rulesMatchedCount: number;

	decisionReasonCode: string | null;
	decisionReasonLabel: string | null;
	assignedToName: string | null;

	applicant: Record<string, any>;
	providerResults: Record<string, any>[];
	activity: IActivity[];

	txnDate: string;
	statusChangedAt: string | null;
	createdAt: string;

	/** Present on `GET /transactions/:id`, which joins the alerts. */
	alerts?: IAlert[];
}

/** `GET /transaction-monitoring/alerts` — one page plus the tab badges. */
export interface IAlertQueue {
	data: IAlert[];
	counts: {
		open: number;
		investigating: number;
		awaiting: number;
		sar: number;
		done: number;
		all: number;
		/* An assignee count, not a status count — which is why the service
		   counts it separately and why the tab badged `00` before it did. */
		mine: number;
	};
	/** Rows on this page AFTER the post-populate filters. */
	shown: number;
	/** Rows matching the tab in mongo — what the pager divides into pages. */
	total: number;
	pageNo: number;
	pageSize: number;
}

export interface IBin {
	t: number;
	n: number;
	ok: number;
	rev: number;
	rej: number;
	run: number;
	vol: number;
	p95: number;
	max: number;
	avg: number;
}

/** `GET /transaction-monitoring/transaction-stats` — the whole header, one call. */
export interface ITxnStats {
	window: { minutes: number; since: string };
	bins: IBin[];
	tiles: {
		openAlerts: number;
		oldestOpenAt: string | null;
		resolved: number;
		autoDeclined: number;
		awaitingUser: number;
	};
	hourMeter: {
		total: number;
		resolved: number;
		investigating: number;
		awaiting: number;
		open: number;
		dismissed: number;
	};
	rulesFiring: { ruleName: string; count: number }[];
	throughput: { transactions: number; perMinute: number };
}

export interface IMonitoringRule {
	_id: string;
	ruleUuid: string;
	ruleName: string;
	category: string;
	bundle: string;
	predicate: string;
	scoreDelta: number;
	mode: IRuleMode;
	actions: string[];
	enabled: boolean;
	tags: { name: string; color: string }[];
	changeStatus: ITxnStatus | null;
	/** The code a `change_status` rule stamps on the transaction it decides. */
	decisionReasonCode: string | null;
	/**
	 * True when this row is the tenant's own copy of a shared default — i.e.
	 * somebody has already retuned it here. The shared catalogue rows carry no
	 * businessId and read as inherited.
	 */
	overridden: boolean;
	/**
	 * Whether the engine has an implementation for this row's `predicate`.
	 *
	 * `false` means the rule is seeded and skipped on every transaction — it can
	 * never fire, and without saying so it looks exactly like a rule that has
	 * simply never matched. Stamped by the service against the engine's own
	 * PREDICATES map, so the two cannot drift.
	 */
	predicateImplemented: boolean;
}

/** Didit's defaults, and what the service scores against. */
export const THRESHOLDS = { review: 60, decline: 85 };

export const severityOf = (score: number): ISeverity =>
	score < 10
		? 'UNKNOWN'
		: score < 40
			? 'LOW'
			: score < 70
				? 'MEDIUM'
				: score < 90
					? 'HIGH'
					: 'CRITICAL';

/**
 * ONE HISTORICAL IMPORT, as the log table and the detail modal read it.
 *
 * `rejectedRows` is deliberately absent: the list endpoint projects it away
 * because it can be thousands of entries and is only ever needed by the error
 * report download, which asks for it separately.
 */
export type IImportStatus =
	| 'queued'
	| 'validating'
	| 'importing'
	| 'needs_attention'
	| 'done'
	| 'partially_failed'
	| 'cancelled'
	| 'failed';

export interface IImportBatch {
	_id: string;
	label: string;
	filename: string;
	uploadedBy: string | null;
	status: IImportStatus;
	/** Why the run paused or died — named plainly, never just "needs attention". */
	pauseReason: string | null;

	rowsTotal: number;
	rowsProcessed: number;
	rowsImported: number;
	/** Rows whose txnId was already stored — what makes a re-run safe. */
	rowsAlreadyExisted: number;
	rowsRejected: number;
	rowsWarned: number;
	alertsRaised: number;

	rejectReasons: {
		code: string;
		detail: string;
		count: number;
		sampleRow: number;
		sampleValue: string;
	}[];

	/** Informational only — the pipeline sorts regardless of how the file arrived. */
	sortedOnArrival: boolean | null;
	rowsReordered: number;
	ratePerSec: number;

	cancelRequested: boolean;
	startedAt: string | null;
	finishedAt: string | null;
	createdAt: string;

	/** Only on the detail response, never on the list. */
	findings?: {
		bySeverity: Record<string, number>;
		byStatus: Record<string, number>;
		/** How many of this batch's findings are now live triage work. */
		promoted: number;
	};
}

/** One effective-dated FX rate. */
export interface IFxRate {
	_id: string;
	fromCurrency: string;
	toCurrency: string;
	rate: number;
	/** In force from this date until the next rate for the same pair. */
	effectiveFrom: string;
	source: string;
	note: string | null;
}

/** A pair summarised — how many rates it has and the span they cover. */
export interface IFxPair {
	from: string;
	to: string;
	count: number;
	earliest: string;
	latest: string;
}

/** One party as it will be filed (a FinCEN SAR carries six). */
export interface ISarParty {
	role: 'transmitter' | 'transmitterContact' | 'filingInstitution' | 'activityInstitution' | 'contactOffice' | 'subject';
	isEntity?: boolean;
	name?: { first?: string; middle?: string; last?: string; full?: string };
	address?: { street?: string; city?: string; state?: string; zip?: string; country?: string };
	phone?: string;
	identification?: { number?: string; typeCode?: string };
	identifications?: { number?: string; typeCode?: string; issuerState?: string; issuerCountry?: string }[];
	tinUnknown?: boolean;
	birthDate?: string;
}

export interface ISarGap {
	field: string;
	why: string;
}

/** The pre-filled SAR draft — mirrors `SarDraft` in transaction.monitoring.api/src/libs/fincen/sar-prefill.ts. */
export interface ISarDraft {
	generatedAt: string;
	recommended: boolean;
	shadowOnly: boolean;
	recommendation: string;
	status: 'ready' | 'needs_information' | 'no_activity' | 'shadow_only';
	caseNumber: string | null;
	caseId: string | null;
	transactions: {
		_id: string;
		txnId: string | null;
		date: string | null;
		amount: string | null;
		currency: string | null;
		usd: number | null;
		status: string | null;
		score: number | null;
		decisionReasonCode: string | null;
		decisionReasonLabel: string | null;
	}[];
	rules: {
		ruleUuid: string;
		ruleName: string;
		mode: string;
		scoreDelta: number;
		evidence: Record<string, unknown>;
		typeId: string | null;
		subtypeId: string | null;
		classificationLabel: string | null;
		rationale: string | null;
	}[];
	classificationDetails: {
		typeId: string;
		typeLabel: string;
		subtypeId: string;
		subtypeLabel: string;
		otherText: string | null;
		ruleUuids: string[];
	}[];
	activity: {
		filingDate: string;
		activityFromDate: string;
		activityToDate?: string;
		totalAmount?: number;
		amountUnknown?: boolean;
		classifications: { typeId: string; subtypeId: string; otherText?: string }[];
		narrative: string;
		parties: ISarParty[];
	};
	fields: { section: string; label: string; value: string; source: string }[];
	gaps: ISarGap[];
	advisories: ISarGap[];
	kyc: {
		linked: boolean;
		sessionId: string | null;
		how: string | null;
		candidates: { sessionId: string; name: string | null; email: string | null; createdAt: string | null }[];
	};
	validation: string[];
	xml?: string;
}

export interface IAssignee {
	_id: string;
	firstName?: string;
	lastName?: string;
	email?: string;
}

/** One FinCEN response file applied to a submission (see ack-parse in the service). */
export interface IFincenResponse {
	name: string;
	kind: 'messages' | 'ack' | 'other';
	fetchedAt: string;
	statusCode: string | null;
	summary: string | null;
	bsaIds: string[];
	fatalErrors: unknown[];
	warnings: unknown[];
	needsReview: unknown[];
	parseError: string | null;
}

export type IFincenSubmissionStatus =
	| 'SUBMITTED'
	| 'MESSAGES_RECEIVED'
	| 'ACCEPTED'
	| 'ACCEPTED_WITH_WARNINGS'
	| 'REJECTED'
	| 'NEEDS_REVIEW';

/**
 * One SAR batch sent over SDTM — mirrors `tm-fincen-submissions`.
 * `environment: 'test'` is the BSA E-Filing sandbox: NOTHING sent there is a filing.
 */
export interface IFincenSubmission {
	_id: string;
	caseId: string;
	caseNumber: string | null;
	environment: 'test' | 'production';
	remoteName: string;
	submittedAt: string;
	submittedBy: string | null;
	status: IFincenSubmissionStatus;
	bsaId: string | null;
	responses: IFincenResponse[];
	lastPolledAt: string | null;
}

/** Whether this service can reach FinCEN at all, and which system it would reach. */
export interface IFincenConnection {
	configured: boolean;
	environment: 'test' | 'production' | null;
	host: string | null;
	sdtmUid?: string | null;
	hostKeyPinned?: boolean;
}

/** `GET /sar-filer-profiles` — the profile is `{}` when nobody has entered one. */
export interface ISarFilerProfileResp {
	businessId: string;
	profile: Record<string, any>;
	updatedAt: string | null;
}
